import { Component, type ReactNode, type ErrorInfo } from 'react';
import './ErrorBoundary.css';

/** Props for the ErrorBoundary class component. */
interface Props {
  children: ReactNode;
  /** Optional name of the section, shown in the fallback UI and console warning. */
  sectionName?: string;
  /** Optional custom fallback element to display instead of the default error card. */
  fallback?: ReactNode;
}

/** Internal state tracking whether an error has been caught. */
interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary - React class component that catches JavaScript errors
 * anywhere in its child component tree and displays a fallback UI
 * with a retry button, preventing the whole app from crashing.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  /** React lifecycle: updates state so the next render shows the error UI. */
  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  /** React lifecycle: logs the error and component stack to the console and reports to Sentry.
   * Uses console.warn instead of console.error intentionally: React intercepts
   * console.error during development and shows the error overlay, so using
   * console.warn avoids double-reporting (once here, once in React dev overlay). */
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.warn(
      `[ErrorBoundary${this.props.sectionName ? ` - ${this.props.sectionName}` : ''}]`,
      error,
      info.componentStack
    );

    const globalCapture = typeof window !== 'undefined' ? window.captureException : null;
    if (globalCapture) {
      globalCapture(error, {
        contexts: { react: { componentStack: info.componentStack } },
        tags: { section: this.props.sectionName ?? 'unknown' },
      });
    }
  }

  /** Resets error state so the boundary re-renders its children.
   * This unmounts the error UI and re-mounts the children. If the error was
   * caused by a failed dynamic import, the retry will also fail because the
   * import result is cached by the module system. Only works for transient
   * render errors unrelated to module loading. */
  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  /**
   * Renders the fallback error UI when hasError is true, or the wrapped
   * children when no error has occurred. The error UI includes a warning
   * icon, the section name (if provided), a descriptive message, and a
   * Retry button that calls handleRetry to reset the error state.
   */
  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }
      return (
        <section className={`section-placeholder ${this.props.sectionName ? `error-${this.props.sectionName}` : ''}`}>
          <div className="container">
            <div className="placeholder-card glass-panel error-boundary-card">
              <div className="error-boundary-icon">
                {'\u26A0\uFE0F'}
              </div>
              <h3 className="error-boundary-title">
                {this.props.sectionName
                  ? `Could not load ${this.props.sectionName}`
                  : 'Something went wrong'}
              </h3>
              <p className="error-boundary-desc">
                An unexpected error occurred while loading this section.
                <br />
                Please try again or refresh the page.
              </p>
              <button
                onClick={this.handleRetry}
                className="btn-primary error-boundary-btn"
              >
                Retry
              </button>
            </div>
          </div>
        </section>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
